import React, { useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import GuideViewer from '@/components/guides/GuideViewer';
import { TransformedGuideData } from '@/types';
import { GuideBuilderFormData } from './schemas';
import { GuideStep, StepTranslation, GuideLanguage } from '@/services/apis/guides';

interface GuidePreviewerProps {
  guide: GuideBuilderFormData;
}

const GuidePreviewer: React.FC<GuidePreviewerProps> = ({ guide }) => {
  const { currentLanguage } = useLanguage();

  // Transform GuideBuilderFormData to TransformedGuideData format
  const transformedData = useMemo<TransformedGuideData>(() => {
    // Transform steps from GuideBuilderFormData format to GuideStep format
    const transformedSteps: GuideStep[] = guide.steps.map((step) => {
      // Create StepTranslation from step data
      const stepTranslation: StepTranslation = {
        id: `${step.id}-translation`,
        language: (guide.language || currentLanguage) as GuideLanguage,
        title: step.title,
        content: step.content,
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return {
        id: step.id,
        position: step.position,
        type: step.type,
        glossaries: (step as any).glossaries,
        translations: [stepTranslation],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    });

    // Create guide translation
    const guideTranslation = {
      id: `${guide.id || 'preview'}-translation`,
      language: (guide.language || currentLanguage) as GuideLanguage,
      title: guide.title,
      description: guide.description,
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return {
      id: guide.id || 'preview',
      title: guide.title,
      description: guide.description,
      difficulty: guide.difficulty,
      estimatedTime: guide.estimatedTime === '' ? undefined : guide.estimatedTime,
      steps: transformedSteps,
      translations: [guideTranslation],
    };
  }, [guide, currentLanguage]);

  return <GuideViewer guideData={transformedData} guideId={guide.id || 'preview'} isPreviewMode={true} />;
};

export default GuidePreviewer;
